import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Save, Loader2, Package, Star, FolderOpen, X, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import MediaPickerModal from "../../components/MediaPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SIZE_OPTIONS = ["", "small", "medium", "large", "extra-large"];
const STATUS_OPTIONS = ["active", "inactive"];

const LABEL = "block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5";
const FIELD = "w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#1C1A19] outline-none focus:border-seikat-pink/50";

export default function AdminVariants() {
  const [products, setProducts] = useState([]);
  const [activeProductId, setActiveProductId] = useState("");
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mediaPicker, setMediaPicker] = useState(null); // { variantIdx } | null

  const activeProduct = products.find(p => p.product_id === activeProductId);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/products?limit=100`);
      const list = r.data.products || [];
      setProducts(list);
      if (list.length > 0) setActiveProductId(list[0].product_id);
    } catch {
      toast.error("Gagal memuat produk");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    if (!activeProductId) return;
    axios.get(`${API}/admin/products/${activeProductId}/variants`, { withCredentials: true })
      .then(r => setVariants(r.data.variants || []))
      .catch(() => setVariants([]));
  }, [activeProductId]);

  const addVariant = () => {
    setVariants(v => [...v, {
      variant_id: `new_${Date.now()}_${v.length}`,
      size: "medium", color: "", sku: "",
      price: activeProduct?.price || 0,
      stock: 10, track_stock: true,
      images: [], is_default: v.length === 0, status: "active"
    }]);
  };

  const removeVariant = (idx) => setVariants(v => v.filter((_, i) => i !== idx));

  const updateVariant = (idx, patch) => setVariants(v => v.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const setDefault = (idx) => setVariants(v => v.map((x, i) => ({ ...x, is_default: i === idx })));

  const save = async () => {
    setSaving(true);
    try {
      const payload = variants.map(v => ({
        ...v,
        variant_id: v.variant_id?.startsWith("new_") ? null : v.variant_id,
        price: Number(v.price || 0),
        stock: Number(v.stock || 0)
      }));
      const r = await axios.put(`${API}/admin/products/${activeProductId}/variants`, { variants: payload }, { withCredentials: true });
      setVariants(r.data.variants || []);
      toast.success(`${r.data.count} varian berhasil disimpan`);
    } catch {
      toast.error("Gagal menyimpan varian");
    }
    setSaving(false);
  };

  const handlePickMedia = (url) => {
    if (mediaPicker === null) return;
    const idx = mediaPicker.variantIdx;
    const v = variants[idx];
    if (!v) return;
    const existing = v.images || [];
    if (!existing.includes(url)) {
      updateVariant(idx, { images: [...existing, url] });
    }
  };

  const removeImage = (variantIdx, imgIdx) => {
    const v = variants[variantIdx];
    updateVariant(variantIdx, { images: v.images.filter((_, i) => i !== imgIdx) });
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>;
  }

  const priceRange = variants.length > 1
    ? { min: Math.min(...variants.map(v => v.price || 0)), max: Math.max(...variants.map(v => v.price || 0)) }
    : null;

  return (
    <div data-testid="admin-variants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-[#1C1A19]">Varian <span className="text-seikat-pink">Produk</span></h1>
          <p className="font-body text-sm text-[#8A857D] mt-1">Kelola ukuran, warna, harga, dan stok per varian</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadProducts} className="w-9 h-9 rounded-xl border border-[#D4859B]/20 flex items-center justify-center text-[#8A857D] hover:text-seikat-pink transition-colors">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* Product picker */}
      <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5 mb-6" data-testid="variants-product-picker">
        <p className="font-body text-[10px] tracking-widest uppercase text-[#8A857D] mb-3">Pilih Produk</p>
        <div className="flex flex-wrap gap-2">
          {products.map(p => {
            const active = activeProductId === p.product_id;
            return (
              <button key={p.product_id} onClick={() => setActiveProductId(p.product_id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-body transition-all ${
                  active ? "bg-seikat-pink text-white shadow-sm" : "bg-[#FDF8F6] border border-[#D4859B]/20 text-[#4A453F] hover:border-seikat-pink/40"
                }`}
                data-testid={`product-pick-${p.product_id}`}>
                <Package size={11} /> {p.name_id || p.name_en || p.title}
                {p.has_variants && <span className="ml-1 text-[8px] tracking-widest uppercase opacity-70">• {p.variants?.length || 0} varian</span>}
              </button>
            );
          })}
        </div>
      </div>

      {activeProduct && (
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6" data-testid="variants-editor">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <p className="font-body text-[10px] tracking-widest uppercase text-[#8A857D]">{activeProduct.name_id || activeProduct.name_en || activeProduct.title}</p>
              <h2 className="font-heading text-2xl text-[#1C1A19] mt-1">
                {variants.length} varian
                {priceRange && priceRange.min !== priceRange.max && (
                  <span className="font-body text-base text-seikat-pink ml-2">
                    Rp {priceRange.min.toLocaleString("id-ID")} — {priceRange.max.toLocaleString("id-ID")}
                  </span>
                )}
              </h2>
              <p className="font-body text-xs text-[#A8A29A] mt-0.5">Harga dasar: Rp {activeProduct.price?.toLocaleString("id-ID")}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={addVariant} data-testid="variants-add"
                className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#4A453F] hover:border-seikat-pink/40 hover:text-seikat-pink transition-colors">
                <Plus size={13} /> Tambah Varian
              </button>
              <button onClick={save} disabled={saving} data-testid="variants-save"
                className="flex items-center gap-1.5 px-4 py-2 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Simpan Semua
              </button>
            </div>
          </div>

          {variants.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-[#D4859B]/20 rounded-2xl" data-testid="variants-empty">
              <Package size={28} className="mx-auto mb-3 text-seikat-pink/40" />
              <p className="font-heading text-xl text-[#1C1A19]">Belum ada varian</p>
              <p className="font-body text-sm text-[#A8A29A] mt-2">Klik "Tambah Varian" untuk membuat ukuran/warna pertama.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {variants.map((v, idx) => (
                  <motion.div key={v.variant_id} layout initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.3 }}
                    className={`p-5 rounded-2xl border transition-all ${v.is_default ? "border-seikat-pink/40 bg-seikat-pink/3" : "border-[#D4859B]/15 bg-[#FDF8F6]"}`}
                    data-testid={`variant-row-${idx}`}
                  >
                    {/* Top row: default star + fields */}
                    <div className="flex items-start gap-4">
                      {/* Default star */}
                      <div className="flex flex-col items-center gap-1 pt-1 flex-shrink-0">
                        <button onClick={() => setDefault(idx)}
                          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${v.is_default ? "bg-seikat-pink text-white shadow-sm" : "bg-white border border-[#D4859B]/20 text-[#A8A29A] hover:text-seikat-pink"}`}
                          title="Jadikan default" data-testid={`variant-default-${idx}`}>
                          <Star size={13} fill={v.is_default ? "currentColor" : "none"} />
                        </button>
                        {v.is_default && <span className="text-[8px] tracking-widest uppercase text-seikat-pink">Default</span>}
                      </div>

                      {/* Main fields */}
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <div>
                          <label className={LABEL}>Ukuran</label>
                          <select value={v.size || ""} onChange={e => updateVariant(idx, { size: e.target.value })} className={FIELD} data-testid={`variant-size-${idx}`}>
                            {SIZE_OPTIONS.map(s => <option key={s} value={s}>{s || "—"}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={LABEL}>Warna</label>
                          <input type="text" value={v.color || ""} onChange={e => updateVariant(idx, { color: e.target.value })}
                            placeholder="pink, blush..." className={FIELD} data-testid={`variant-color-${idx}`} />
                        </div>
                        <div>
                          <label className={LABEL}>Harga (Rp)</label>
                          <input type="number" value={v.price || ""} onChange={e => updateVariant(idx, { price: e.target.value })}
                            className={FIELD} data-testid={`variant-price-${idx}`} />
                        </div>
                        <div>
                          <label className={LABEL}>Stok</label>
                          <input type="number" value={v.stock ?? 0} onChange={e => updateVariant(idx, { stock: e.target.value })}
                            className={FIELD} data-testid={`variant-stock-${idx}`} />
                        </div>
                        <div>
                          <label className={LABEL}>SKU</label>
                          <input type="text" value={v.sku || ""} onChange={e => updateVariant(idx, { sku: e.target.value })}
                            placeholder="SKU-001" className={FIELD} data-testid={`variant-sku-${idx}`} />
                        </div>
                        <div>
                          <label className={LABEL}>Status</label>
                          <select value={v.status || "active"} onChange={e => updateVariant(idx, { status: e.target.value })} className={FIELD} data-testid={`variant-status-${idx}`}>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === "active" ? "Aktif" : "Nonaktif"}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Remove button */}
                      <button onClick={() => removeVariant(idx)}
                        className="flex-shrink-0 w-8 h-8 mt-5 rounded-full hover:bg-red-50 text-[#A8A29A] hover:text-red-500 flex items-center justify-center transition-colors"
                        title="Hapus" data-testid={`variant-remove-${idx}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>

                    {/* Images section */}
                    <div className="mt-4 ml-[52px]">
                      <div className="flex items-center justify-between mb-2">
                        <label className={LABEL}>Foto Varian ({v.images?.length || 0})</label>
                        <button type="button" onClick={() => setMediaPicker({ variantIdx: idx })}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#D4859B]/20 text-[#4A453F] hover:border-seikat-pink/40 hover:text-seikat-pink transition-colors text-xs font-body"
                          data-testid={`variant-pick-media-${idx}`}>
                          <FolderOpen size={12} /> Pilih dari Galeri
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2" data-testid={`variant-images-${idx}`}>
                        {(v.images || []).map((img, imgIdx) => (
                          <div key={imgIdx} className="relative group w-16 h-16 rounded-xl overflow-hidden border border-[#D4859B]/15">
                            <img src={img.startsWith("http") ? img : `${BACKEND}${img}`} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => removeImage(idx, imgIdx)}
                              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <X size={12} className="text-white" />
                            </button>
                          </div>
                        ))}
                        {(v.images || []).length === 0 && (
                          <p className="font-body text-xs text-[#C4B8AF] italic">Belum ada foto untuk varian ini</p>
                        )}
                      </div>
                      {/* Also allow manual URL input */}
                      <input type="text"
                        placeholder="Atau paste URL gambar, pisahkan dengan koma..."
                        className="mt-2 w-full px-3 py-2 bg-white border border-[#D4859B]/15 rounded-xl text-xs font-body text-[#6A6560] placeholder:text-[#C4B8AF] outline-none focus:border-seikat-pink/40"
                        onBlur={e => {
                          const urls = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                          if (urls.length > 0) {
                            updateVariant(idx, { images: [...(v.images || []), ...urls.filter(u => !(v.images || []).includes(u))] });
                            e.target.value = "";
                          }
                        }}
                      />
                    </div>

                    {/* Variant summary tag */}
                    {(v.size || v.color) && (
                      <div className="mt-3 ml-[52px] flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#D4859B]/20 text-[10px] font-body text-[#4A453F]">
                          {v.is_default && <Star size={9} fill="#D4859B" className="text-seikat-pink" />}
                          {[v.size, v.color].filter(Boolean).join(" / ")}
                          {v.price ? ` · Rp ${Number(v.price).toLocaleString("id-ID")}` : ""}
                          {v.stock != null ? ` · ${v.stock} pcs` : ""}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full border text-[9px] font-body uppercase ${
                          v.status === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-500 border-gray-200"
                        }`}>{v.status === "active" ? "Aktif" : "Nonaktif"}</span>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Save reminder */}
              <div className="flex items-center justify-between p-4 bg-seikat-pink/5 rounded-2xl border border-seikat-pink/15">
                <p className="font-body text-sm text-[#4A453F]">
                  {variants.length} varian siap disimpan untuk <em className="text-seikat-pink">{activeProduct.name_id || activeProduct.title}</em>
                </p>
                <button onClick={save} disabled={saving} data-testid="variants-save-bottom"
                  className="flex items-center gap-1.5 px-4 py-2 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-60">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  Simpan
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <MediaPickerModal
        open={!!mediaPicker}
        onClose={() => setMediaPicker(null)}
        onSelect={handlePickMedia}
        accept="image"
      />
    </div>
  );
}
